import { initializeApp, FirebaseApp } from 'firebase/app'
import type { Auth } from 'firebase/auth'
import type { Analytics } from 'firebase/analytics'
import { getFirebaseRuntimeConfig } from './firebaseRuntimeConfig'

const firebaseConfig = getFirebaseRuntimeConfig()

// Firebase가 설정되어 있는지 확인
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.apiKey !== 'undefined' &&
    firebaseConfig.apiKey !== '' &&
    !firebaseConfig.apiKey.includes('your-') &&
    firebaseConfig.projectId &&
    firebaseConfig.projectId !== 'undefined'
)

// Firebase 초기화 (설정이 있는 경우에만)
let app: FirebaseApp | null = null
let auth: Auth | null = null
let analytics: Analytics | null = null
const analyticsEnabled =
  import.meta.env.PROD && import.meta.env.VITE_ENABLE_ANALYTICS === 'true'

const initializeAnalytics = async () => {
  if (
    !analyticsEnabled ||
    !app ||
    analytics ||
    !firebaseConfig.measurementId ||
    typeof window === 'undefined'
  ) {
    return
  }

  try {
    const { getAnalytics, isSupported } = await import('firebase/analytics')
    const supported = await isSupported()
    if (supported && app) {
      analytics = getAnalytics(app)
    }
  } catch {
    // Analytics not supported in this environment
  }
}

const scheduleAnalyticsInitialization = () => {
  if (
    !analyticsEnabled ||
    !firebaseConfig.measurementId ||
    typeof window === 'undefined'
  ) {
    return
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(
      () => {
        void initializeAnalytics()
      },
      { timeout: 3000 }
    )
    return
  }

  globalThis.setTimeout(() => {
    void initializeAnalytics()
  }, 1500)
}

const initializeAuth = async () => {
  if (!app || auth) {
    return auth
  }

  try {
    const { getAuth, connectAuthEmulator } = await import('firebase/auth')
    auth = getAuth(app)

    if (firebaseConfig.useEmulator && import.meta.env.DEV) {
      try {
        connectAuthEmulator(auth, 'http://localhost:9098')
      } catch {
        // Emulator already connected
      }
    }
  } catch (error) {
    console.error('Firebase Auth 초기화 실패:', error)
    auth = null
  }

  return auth
}

export const getFirebaseAuth = async (): Promise<Auth | null> => {
  if (!isFirebaseConfigured || !app) {
    return null
  }

  return initializeAuth()
}

if (isFirebaseConfigured) {
  try {
    app = initializeApp(firebaseConfig)

    // Analytics 초기화 (선택사항)
    scheduleAnalyticsInitialization()
  } catch (error) {
    console.error('Firebase 초기화 실패:', error)
    app = null
    auth = null
  }
}

export { auth, analytics }
export default app
